"use client";

import * as React from "react";
import { en, type LocaleDict } from "./en";
import { zhCN } from "./zh-CN";
import {
  DEFAULT_LOCALE,
  type AppLocale,
  detectLocale,
  deepMergeMessages,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  interpolate,
  persistLocale,
  readStoredLocale,
} from "./runtime";

const catalogs: Record<AppLocale, LocaleDict> = {
  en,
  "zh-CN": deepMergeMessages(
    en as unknown as Record<string, unknown>,
    zhCN as unknown as Record<string, unknown>,
  ) as LocaleDict,
};

type LocaleContextValue = {
  locale: AppLocale;
  messages: LocaleDict;
  setLocale: (locale: AppLocale) => void;
  t: (
    template: string,
    vars?: Record<string, string | number | null | undefined>,
  ) => string;
  formatDateTime: (
    value: string | number | Date | null | undefined,
    timeZone?: string,
  ) => string;
  formatDate: (
    value: string | number | Date | null | undefined,
    timeZone?: string,
  ) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (amountMinor: number, currencyCode: string) => string;
  statusLabel: (
    kind: keyof LocaleDict["statuses"],
    code: string,
  ) => string;
};

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

function applyDocumentLang(locale: AppLocale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<AppLocale>(DEFAULT_LOCALE);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const initial = detectLocale({
      saved: readStoredLocale(),
      navigatorLanguage:
        typeof navigator !== "undefined" ? navigator.language : null,
    });
    setLocaleState(initial);
    applyDocumentLang(initial);
    setReady(true);
  }, []);

  const setLocale = React.useCallback((next: AppLocale) => {
    setLocaleState(next);
    persistLocale(next);
    applyDocumentLang(next);
  }, []);

  const messages = catalogs[locale] ?? catalogs.en;

  const value = React.useMemo<LocaleContextValue>(
    () => ({
      locale,
      messages,
      setLocale,
      t: (template, vars) => interpolate(template, vars),
      formatDateTime: (v, tz) => formatDateTime(v, locale, tz),
      formatDate: (v, tz) => formatDate(v, locale, tz),
      formatNumber: (v, options) => formatNumber(v, locale, options),
      formatCurrency: (amount, code) => formatCurrency(amount, code, locale),
      statusLabel: (kind, code) => {
        const map = messages.statuses[kind] as Record<string, string>;
        const fromActive = map[code];
        if (fromActive) return fromActive;
        const enMap = catalogs.en.statuses[kind] as Record<string, string>;
        return enMap[code] ?? code;
      },
    }),
    [locale, messages, setLocale],
  );

  if (!ready) {
    return (
      <LocaleContext.Provider
        value={{
          ...value,
          locale: DEFAULT_LOCALE,
          messages: catalogs.en,
        }}
      >
        {children}
      </LocaleContext.Provider>
    );
  }

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = React.useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocaleContext must be used within LocaleProvider");
  }
  return ctx;
}

export function useMessages(): LocaleDict {
  return useLocaleContext().messages;
}

export function useLocale(): AppLocale {
  return useLocaleContext().locale;
}

export function useSetLocale(): (locale: AppLocale) => void {
  return useLocaleContext().setLocale;
}
